/**
 * Stripe charge/price amount rules (for form validation).
 * @see https://docs.stripe.com/currencies
 *
 * - Most currencies: 2 decimal places; API amount = integer cents.
 * - Zero-decimal currencies: whole numbers only; API amount = integer major units.
 * - unit_amount must be an integer — fractional cents (e.g. 999.9) are NOT accepted.
 */

/**
 * Currencies Stripe accepts for Prices / charges (lowercase ISO codes).
 * Keep in sync with Stripe "Invalid currency" responses / docs.
 */
export const STRIPE_SUPPORTED_CURRENCIES = [
  "usd",
  "aed",
  "afn",
  "all",
  "amd",
  "ang",
  "aoa",
  "ars",
  "aud",
  "awg",
  "azn",
  "bam",
  "bbd",
  "bdt",
  "bgn",
  "bhd",
  "bif",
  "bmd",
  "bnd",
  "bob",
  "brl",
  "bsd",
  "bwp",
  "byn",
  "bzd",
  "cad",
  "cdf",
  "chf",
  "clp",
  "cny",
  "cop",
  "crc",
  "cve",
  "czk",
  "djf",
  "dkk",
  "dop",
  "dzd",
  "egp",
  "etb",
  "eur",
  "fjd",
  "fkp",
  "gbp",
  "gel",
  "gip",
  "gmd",
  "gnf",
  "gtq",
  "gyd",
  "hkd",
  "hnl",
  "hrk",
  "htg",
  "huf",
  "idr",
  "ils",
  "inr",
  "isk",
  "jmd",
  "jod",
  "jpy",
  "kes",
  "kgs",
  "khr",
  "kmf",
  "krw",
  "kwd",
  "kyd",
  "kzt",
  "lak",
  "lbp",
  "lkr",
  "lrd",
  "lsl",
  "mad",
  "mdl",
  "mga",
  "mkd",
  "mmk",
  "mnt",
  "mop",
  "mur",
  "mvr",
  "mwk",
  "mxn",
  "myr",
  "mzn",
  "nad",
  "ngn",
  "nio",
  "nok",
  "npr",
  "nzd",
  "omr",
  "pab",
  "pen",
  "pgk",
  "php",
  "pkr",
  "pln",
  "pyg",
  "qar",
  "ron",
  "rsd",
  "rub",
  "rwf",
  "sar",
  "sbd",
  "scr",
  "sek",
  "sgd",
  "shp",
  "sle",
  "sos",
  "srd",
  "std",
  "szl",
  "thb",
  "tjs",
  "tnd",
  "top",
  "try",
  "ttd",
  "twd",
  "tzs",
  "uah",
  "ugx",
  "uyu",
  "uzs",
  "vnd",
  "vuv",
  "wst",
  "xaf",
  "xcd",
  "xcg",
  "xof",
  "xpf",
  "yer",
  "zar",
  "zmw",
  "usdc",
  "btn",
  "ghs",
  "eek",
  "lvl",
  "svc",
  "vef",
  "ltl",
  "sll",
  "mro",
];

const STRIPE_SUPPORTED_CURRENCY_SET: { [code: string]: true } = STRIPE_SUPPORTED_CURRENCIES.reduce(
  (acc, code) => {
    acc[code] = true;
    return acc;
  },
  {} as { [code: string]: true }
);

/** Currencies Stripe treats as zero-decimal (no minor unit in API amounts). */
export const STRIPE_ZERO_DECIMAL_CURRENCIES = [
  "bif",
  "clp",
  "djf",
  "gnf",
  "jpy",
  "kmf",
  "krw",
  "mga",
  "pyg",
  "rwf",
  "ugx",
  "vnd",
  "vuv",
  "xaf",
  "xof",
  "xpf",
];

/** Minimum charge in major units (settlement-currency mins from Stripe docs). */
export const STRIPE_MIN_MAJOR_BY_CURRENCY: { [code: string]: number } = {
  usd: 0.5,
  aed: 2,
  ars: 0.5,
  aud: 0.5,
  brl: 0.5,
  cad: 0.5,
  chf: 0.5,
  cop: 0.5,
  czk: 15,
  dkk: 2.5,
  eur: 0.5,
  gbp: 0.3,
  hkd: 4,
  huf: 175,
  idr: 0.5,
  ils: 0.5,
  inr: 0.5,
  jpy: 50,
  krw: 50,
  mxn: 10,
  myr: 2,
  nok: 3,
  nzd: 0.5,
  php: 0.5,
  pln: 2,
  ron: 2,
  rub: 0.5,
  sek: 3,
  sgd: 0.5,
  thb: 10,
  zar: 0.5,
  vnd: 12000,
};

/** Default min when currency is unknown — use USD floor. */
export const STRIPE_DEFAULT_MIN_MAJOR = 0.5;

/** Max digits Stripe allows for amount in smallest unit (8 digits). */
export const STRIPE_MAX_SMALLEST_UNIT = 99999999;

export function normalizeCurrencyCode(currency: any): string {
  return currency ? String(currency).trim().toLowerCase() : "";
}

export function isValidStripeCurrencyFormat(currency: any): boolean {
  return /^[a-z]{3}$/i.test(normalizeCurrencyCode(currency));
}

export function isStripeSupportedCurrency(currency: any): boolean {
  const code = normalizeCurrencyCode(currency);
  return !!code && !!STRIPE_SUPPORTED_CURRENCY_SET[code];
}

export function isValidStripeCurrencyCode(currency: any): boolean {
  return isValidStripeCurrencyFormat(currency) && isStripeSupportedCurrency(currency);
}


export function isStripeZeroDecimalCurrency(currency: any): boolean {
  return STRIPE_ZERO_DECIMAL_CURRENCIES.indexOf(normalizeCurrencyCode(currency)) !== -1;
}

export function getStripeMinMajorAmount(currency: any): number {
  const code = normalizeCurrencyCode(currency);
  if (code && STRIPE_MIN_MAJOR_BY_CURRENCY[code] != null) {
    return STRIPE_MIN_MAJOR_BY_CURRENCY[code];
  }
  return STRIPE_DEFAULT_MIN_MAJOR;
}

export function getStripeMaxMajorAmount(currency: any): number {
  if (isStripeZeroDecimalCurrency(currency)) {
    return STRIPE_MAX_SMALLEST_UNIT;
  }
  return STRIPE_MAX_SMALLEST_UNIT / 100;
}

export function countDecimalPlaces(value: number | string): number {
  const raw = String(value).trim();
  if (!raw || raw.indexOf(".") === -1) {
    return 0;
  }
  const fraction = raw.split(".")[1] || "";
  return fraction.replace(/0+$/, "").length > 0 ? fraction.length : fraction.replace(/0+$/, "").length;
}

/** Strict decimal-place count from numeric value (avoids "9.990" string quirks). */
export function hasMoreThanMaxDecimals(value: number | string, maxDecimals: number): boolean {
  const num = Number(value);
  if (!Number.isFinite(num)) {
    return true;
  }
  const scaled = num * Math.pow(10, maxDecimals);
  return Math.abs(scaled - Math.round(scaled)) > 1e-8;
}

export type StripePriceValidationError =
  | "required"
  | "not_a_number"
  | "not_positive"
  | "too_many_decimals"
  | "below_minimum"
  | "above_maximum";

export function validateStripeMajorAmount(
  amount: any,
  currency: any
): StripePriceValidationError | null {
  if (amount == null || amount === "") {
    return "required";
  }

  const value = Number(amount);
  if (!Number.isFinite(value)) {
    return "not_a_number";
  }

  if (value <= 0) {
    return "not_positive";
  }

  const maxDecimals = isStripeZeroDecimalCurrency(currency) ? 0 : 2;
  if (hasMoreThanMaxDecimals(value, maxDecimals)) {
    return "too_many_decimals";
  }

  const min = getStripeMinMajorAmount(currency);
  if (value < min) {
    return "below_minimum";
  }

  const max = getStripeMaxMajorAmount(currency);
  if (value > max) {
    return "above_maximum";
  }

  return null;
}

export function stripePriceErrorMessage(
  error: StripePriceValidationError | null,
  currency: any,
  label: string
): string {
  if (!error) {
    return "";
  }

  const code = normalizeCurrencyCode(currency).toUpperCase() || "USD";
  const min = getStripeMinMajorAmount(currency);
  const max = getStripeMaxMajorAmount(currency);
  const maxDecimals = isStripeZeroDecimalCurrency(currency) ? 0 : 2;

  switch (error) {
    case "required":
      return "Enter a " + label + " price";
    case "not_a_number":
      return "Enter a valid number for " + label + " price";
    case "not_positive":
      return label + " price must be greater than 0";
    case "too_many_decimals":
      return maxDecimals === 0
        ? label + " price must be a whole number for " + code
        : label + " price allows at most " + maxDecimals + " decimal places (Stripe)";
    case "below_minimum":
      return label + " price must be at least " + min + " " + code + " (Stripe minimum)";
    case "above_maximum":
      return label + " price must be at most " + max + " " + code;
    default:
      return "Invalid " + label + " price";
  }
}

export function validateStripeCurrency(currency: any): string | null {
  if (!currency || String(currency).trim().length === 0) {
    return "Currency is required";
  }
  if (!isValidStripeCurrencyFormat(currency)) {
    return "Currency must be a 3-letter ISO code (e.g. USD, VND)";
  }
  if (!isStripeSupportedCurrency(currency)) {
    const code = normalizeCurrencyCode(currency).toUpperCase();
    return "Invalid currency: " + code + ". Enter a Stripe-supported code (e.g. USD, VND)";
  }
  return null;
}

/**
 * Convert display major units → Stripe unit_amount (smallest currency unit).
 * USD 9.99 → 999; VND/JPY (zero-decimal) 12000 → 12000.
 */
export function toStripeSmallestUnit(amount: number | string, currency?: any): number {
  const value = Number(amount);
  if (!Number.isFinite(value)) {
    return 0;
  }
  if (isStripeZeroDecimalCurrency(currency)) {
    return Math.round(value);
  }
  return Math.round(value * 100);
}

/** Convert Stripe unit_amount → display major units. USD 999 → 9.99; VND 12000 → 12000. */
export function fromStripeSmallestUnit(
  amount: number | string | null | undefined,
  currency?: any
): number | null {
  if (amount == null || amount === "") {
    return null;
  }
  const value = Number(amount);
  if (!Number.isFinite(value)) {
    return null;
  }
  if (isStripeZeroDecimalCurrency(currency)) {
    return value;
  }
  return value / 100;
}
